/**
 * Knowledge Categories Module
 * 
 * Allows experts to manage knowledge categories for the Context Manager.
 * Categories are used to classify incoming messages and retrieve relevant
 * scenarios, rules, and directives.
 */

import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import useAuthenticatedAxios from '../../../auth/AuthenticatedAxios';
import { useAuth0 } from '@auth0/auth0-react';
import ImavenLoadingScreen from '../../../Components/Helpers/loading-screen';
import { useAIAssistant } from '../../../Components/AIAssistant';
import { useToast } from '../../../Context/ToastContext';
import { useConfirm } from '../../../Context/ConfirmContext';

// ============================================
// SVG ICONS (Simple, monochrome style)
// ============================================

const Icons = {
  refresh: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
  plus: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  search: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  save: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  loader: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>,
  x: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  book: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
};

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const ModuleContainer = styled.div`
  padding: 24px;
  animation: ${fadeIn} 0.3s ease-out;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  
  /* Hide scrollbar but keep scroll functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #f8fafc;
  margin: 0;
`;

const Subtitle = styled.p`
  color: rgba(248, 250, 252, 0.6);
  font-size: 14px;
  margin: 4px 0 0 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  ${props => props.primary && css`
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    border: none;
    color: white;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
    }
  `}
  
  ${props => props.secondary && css`
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #f8fafc;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  `}
  
  ${props => props.danger && css`
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ef4444;
    
    &:hover {
      background: rgba(239, 68, 68, 0.2);
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
`;

const CategoryCard = styled.div`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9));
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 20px;
  animation: ${fadeIn} 0.3s ease-out;
  animation-delay: ${props => props.delay || 0}ms;
  animation-fill-mode: backwards;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    border-color: ${props => props.color || '#6366f1'}40;
    box-shadow: 0 4px 20px ${props => props.color || '#6366f1'}20;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const CategoryIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.color || '#6366f1'}20;
  color: ${props => props.color || '#6366f1'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;

const CategoryActions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: rgba(248, 250, 252, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #f8fafc;
  }
  
  &.delete:hover {
    background: rgba(239, 68, 68, 0.2);
    border-color: rgba(239, 68, 68, 0.3);
    color: #ef4444;
  }
`;

const CategoryName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #f8fafc;
  margin: 0 0 4px 0;
`;

const CategoryDisplayName = styled.span`
  font-size: 12px;
  color: rgba(248, 250, 252, 0.5);
`;

const CategoryDescription = styled.p`
  font-size: 13px;
  color: rgba(248, 250, 252, 0.6);
  margin: 0 0 12px 0;
  line-height: 1.5;
`;

const KeywordsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
`;

const Keyword = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  background: ${props => props.color || '#6366f1'}15;
  color: ${props => props.color || '#6366f1'};
  font-size: 11px;
  font-weight: 500;
`;

const Badge = styled.span`
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => props.default && css`
    background: rgba(99, 102, 241, 0.15);
    color: #a5b4fc;
    border: 1px solid rgba(99, 102, 241, 0.25);
  `}
  
  ${props => props.custom && css`
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
    border: 1px solid rgba(16, 185, 129, 0.25);
  `}
`;

const PriorityBadge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #1e293b, #0f172a);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  animation: ${slideIn} 0.3s ease-out;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #f8fafc;
  margin: 0;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: rgba(248, 250, 252, 0.8);
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.3);
  color: #f8fafc;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #6366f1;
  }
  
  &::placeholder {
    color: rgba(248, 250, 252, 0.4);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.3);
  color: #f8fafc;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #6366f1;
  }
`;

const ColorPicker = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ColorOption = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 2px solid ${props => props.selected ? '#fff' : 'transparent'};
  background: ${props => props.color};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

// Test Panel
const TestPanel = styled.div`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9));
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 20px;
  margin-top: 24px;
`;

const TestTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #f8fafc;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TestResult = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
`;

const ResultItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ResultLabel = styled.span`
  font-size: 12px;
  color: rgba(248, 250, 252, 0.6);
  min-width: 100px;
`;

const ResultValue = styled.span`
  font-size: 14px;
  color: #f8fafc;
  font-weight: 500;
`;
// ============================================
// AVAILABLE COLORS
// ============================================

const COLORS = [
  '#6366f1', // Indigo
  '#10b981', // Emerald
  '#3b82f6', // Blue
  '#ec4899', // Pink
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#8b5cf6', // Violet
  '#14b8a6', // Teal
  '#f97316', // Orange
  '#84cc16', // Lime
];

// ============================================
// MAIN COMPONENT
// ============================================

const KnowledgeCategoriesModule = () => {
  useAuth0(); // Keep hook call for auth context
  const authenticatedAxios = useAuthenticatedAxios();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  
  // AI Assistant context
  const { updateContext } = useAIAssistant();
  
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    triggerKeywords: '',
    synonyms: '',
    priority: 0,
    color: '#6366f1'
  });
  
  // Test state
  const [testMessage, setTestMessage] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  // Update AI Assistant context when state changes
  useEffect(() => {
    updateContext({
      currentModule: 'knowledge-categories',
      moduleName: 'Knowledge Categories',
      moduleDescription: 'Context orchestration & classification',
      selectedElements: {
        editingCategory: editingCategory ? {
          id: editingCategory._id,
          name: editingCategory.displayName,
          isDefault: editingCategory.isDefault
        } : null,
        lastTestResult: testResult ? {
          category: testResult.categoryDisplayName,
          confidence: testResult.confidence
        } : null,
      },
      availableActions: [
        'createCategory',
        'testClassification',
        'resetToDefaults',
        ...(editingCategory ? ['editCategory', 'deleteCategory'] : [])
      ],
      uiState: {
        categoryCount: categories.length,
        showModal,
        isEditing: !!editingCategory,
        isSaving,
        isTesting,
        hasTestMessage: testMessage.trim().length > 0
      }
    });
  }, [categories.length, editingCategory, showModal, isSaving, 
      isTesting, testMessage, testResult, updateContext]);
  
  // Load categories on mount
  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const loadCategories = async () => {
    try {
      setIsLoading(true);
      const response = await authenticatedAxios.get('/context-manager/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const openCreateModal = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      displayName: '',
      description: '',
      triggerKeywords: '',
      synonyms: '',
      priority: 0,
      color: '#6366f1'
    });
    setShowModal(true);
  };
  
  const openEditModal = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      displayName: category.displayName,
      description: category.description || '',
      triggerKeywords: (category.triggerKeywords || []).join(', '),
      synonyms: (category.synonyms || []).join(', '),
      priority: category.priority || 0,
      color: category.color || '#6366f1'
    });
    setShowModal(true);
  };
  
  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      const payload = {
        ...formData,
        triggerKeywords: formData.triggerKeywords.split(',').map(k => k.trim()).filter(k => k),
        synonyms: formData.synonyms.split(',').map(s => s.trim()).filter(s => s),
        priority: parseInt(formData.priority) || 0
      };
      
      if (editingCategory) {
        await authenticatedAxios.put(`/context-manager/categories/${editingCategory._id}`, payload);
      } else {
        // Generate slug from display name
        payload.name = formData.displayName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        await authenticatedAxios.post('/context-manager/categories', payload);
      }
      
      await loadCategories();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving category:', error);
      showToast(error.response?.data?.error || 'Failed to save category', 'error');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDelete = async (category) => {
    const confirmed = await confirm({
      title: 'Delete Category',
      message: `Are you sure you want to delete "${category.displayName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    
    if (!confirmed) return;
    
    try {
      await authenticatedAxios.delete(`/context-manager/categories/${category._id}`);
      await loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      showToast(error.response?.data?.error || 'Failed to delete category', 'error');
    }
  };
  
  const handleReset = async () => {
    const currentCount = categories.length;
    const isUpdating = currentCount < 21;
    
    const confirmed = await confirm({
      title: isUpdating ? 'Update to Latest Categories' : 'Reset to Defaults',
      message: isUpdating 
        ? `This will update your ${currentCount} categories to the latest 21 comprehensive immigration categories. Custom categories will be removed but you can recreate them after.`
        : 'Are you sure you want to reset all categories to defaults? This will remove all custom categories and restore the 21 default categories.',
      confirmText: isUpdating ? 'Update Now' : 'Reset',
      cancelText: 'Cancel',
      type: 'warning'
    });
    
    if (!confirmed) return;
    
    try {
      const response = await authenticatedAxios.post('/context-manager/categories/reset');
      const newCategories = response.data.categories || [];
      
      // Use the response directly if available, otherwise reload
      if (newCategories.length > 0) {
        setCategories(newCategories);
      } else {
        await loadCategories();
      }
      
      const newCount = newCategories.length || categories.length;
      showToast(
        newCount >= 21 
          ? `Successfully updated to ${newCount} categories!` 
          : `Reset complete. Got ${newCount} categories. If less than 21, restart your backend server to get the latest templates.`,
        newCount >= 21 ? 'success' : 'warning'
      );
    } catch (error) {
      console.error('Error resetting categories:', error);
      showToast('Failed to reset categories', 'error');
    }
  };
  
  const handleTest = async () => {
    if (!testMessage.trim()) return;
    
    try {
      setIsTesting(true);
      const response = await authenticatedAxios.post('/context-manager/classify', {
        message: testMessage
      });
      setTestResult(response.data.classification);
    } catch (error) {
      console.error('Error testing classification:', error);
      setTestResult({ error: 'Classification failed' });
    } finally {
      setIsTesting(false);
    }
  };
  
  if (isLoading) {
    return <ImavenLoadingScreen loadingMessage="Loading knowledge categories..." />;
  }
  
  // Check if categories need updating (less than 21 for immigration experts)
  const needsUpdate = categories.length < 21 && categories.length > 0;
  
  return (
    <ModuleContainer>
      <Header>
        <div>
          <Title>{Icons.book} Knowledge Categories</Title>
          <Subtitle>Organize your knowledge base for intelligent context retrieval ({categories.length} categories)</Subtitle>
        </div>
        <ActionButtons>
          <Button secondary onClick={handleReset} style={needsUpdate ? { 
            background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(245, 158, 11, 0.2))',
            borderColor: 'rgba(251, 191, 36, 0.5)',
            color: '#fbbf24'
          } : {}}>
            {Icons.refresh} {needsUpdate ? 'Update to 21 Categories' : 'Reset to Defaults'}
          </Button>
          <Button primary onClick={openCreateModal}>
            {Icons.plus} Add Category
          </Button>
        </ActionButtons>
      </Header>
      
      {/* Update Notice */}
      {needsUpdate && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1), rgba(245, 158, 11, 0.05))',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ fontSize: '20px' }}>⚡</span>
          <div>
            <div style={{ color: '#fbbf24', fontWeight: '600', marginBottom: '4px' }}>
              New Categories Available!
            </div>
            <div style={{ color: 'rgba(248, 250, 252, 0.7)', fontSize: '13px' }}>
              You have {categories.length} categories. Click "Update to 21 Categories" to get all immigration pathway categories
              including Express Entry, PNPs, Family Sponsorship, H&C, Refugee Protection, Citizenship, Compliance, and more.
            </div>
          </div>
        </div>
      )}
      
      <CategoriesGrid>
        {categories.map((category, index) => (
          <CategoryCard key={category._id} delay={index * 50} color={category.color}>
            {category.isDefault ? (
              <Badge default>Default</Badge>
            ) : (
              <Badge custom>Custom</Badge>
            )}
            
            <CategoryHeader>
              <CategoryIcon color={category.color}>
                {category.displayName?.[0] || 'C'}
              </CategoryIcon>
              <CategoryActions>
                <IconButton onClick={() => openEditModal(category)} title="Edit">
                  {Icons.edit}
                </IconButton>
                <IconButton className="delete" onClick={() => handleDelete(category)} title="Delete">
                  {Icons.trash}
                </IconButton>
              </CategoryActions>
            </CategoryHeader>
            
            <CategoryName>{category.displayName}</CategoryName>
            <CategoryDisplayName>{category.name}</CategoryDisplayName>
            
            {category.description && (
              <CategoryDescription>{category.description}</CategoryDescription>
            )}
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
              <PriorityBadge>Priority: {category.priority || 0}</PriorityBadge>
            </div>
            
            {category.triggerKeywords && category.triggerKeywords.length > 0 && (
              <KeywordsList>
                {category.triggerKeywords.slice(0, 5).map((keyword, idx) => (
                  <Keyword key={idx} color={category.color}>{keyword}</Keyword>
                ))}
                {category.triggerKeywords.length > 5 && (
                  <Keyword color={category.color}>+{category.triggerKeywords.length - 5} more</Keyword>
                )}
              </KeywordsList>
            )}
          </CategoryCard>
        ))}
      </CategoriesGrid>
      
      {/* Test Classification Panel */}
      <TestPanel>
        <TestTitle>{Icons.search} Test Classification</TestTitle>
        <p style={{ color: 'rgba(248, 250, 252, 0.6)', fontSize: '13px', marginBottom: '16px' }}>
          Enter a message to test how it would be classified by the Context Manager.
        </p>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Input
            value={testMessage}
            onChange={(e) => setTestMessage(e.target.value)}
            placeholder="e.g., I want to apply for a work permit in Canada"
            style={{ flex: 1 }}
            onKeyPress={(e) => e.key === 'Enter' && handleTest()}
          />
          <Button primary onClick={handleTest} disabled={isTesting || !testMessage.trim()}>
            {isTesting ? <>{Icons.loader} Testing...</> : <>{Icons.search} Classify</>}
          </Button>
        </div>
        
        {testResult && (
          <TestResult>
            {testResult.error ? (
              <ResultItem>
                <ResultLabel>Error:</ResultLabel>
                <ResultValue style={{ color: '#ef4444' }}>{testResult.error}</ResultValue>
              </ResultItem>
            ) : (
              <>
                <ResultItem>
                  <ResultLabel>Category:</ResultLabel>
                  <ResultValue style={{ color: categories.find(c => c.name === testResult.category)?.color || '#6366f1' }}>
                    {testResult.categoryDisplayName}
                  </ResultValue>
                </ResultItem>
                <ResultItem>
                  <ResultLabel>Confidence:</ResultLabel>
                  <ResultValue>{(testResult.confidence * 100).toFixed(1)}%</ResultValue>
                </ResultItem>
                {testResult.keywords && testResult.keywords.length > 0 && (
                  <ResultItem>
                    <ResultLabel>Keywords:</ResultLabel>
                    <ResultValue>{testResult.keywords.join(', ')}</ResultValue>
                  </ResultItem>
                )}
              </>
            )}
          </TestResult>
        )}
      </TestPanel>
      
      {/* Create/Edit Modal */}
      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingCategory ? 'Edit Category' : 'Create Category'}</ModalTitle>
              <IconButton onClick={() => setShowModal(false)}>{Icons.x}</IconButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>Display Name *</Label>
              <Input
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                placeholder="e.g., Work Permits"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Description</Label>
              <TextArea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this category..."
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Trigger Keywords (comma-separated)</Label>
              <TextArea
                value={formData.triggerKeywords}
                onChange={(e) => setFormData({ ...formData, triggerKeywords: e.target.value })}
                placeholder="work permit, employment authorization, lmia..."
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Synonyms (comma-separated)</Label>
              <Input
                value={formData.synonyms}
                onChange={(e) => setFormData({ ...formData, synonyms: e.target.value })}
                placeholder="employment visa, working permit..."
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Priority (higher = more important)</Label>
              <Input
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                placeholder="0"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Color</Label>
              <ColorPicker>
                {COLORS.map((color) => (
                  <ColorOption
                    key={color}
                    color={color}
                    selected={formData.color === color}
                    onClick={() => setFormData({ ...formData, color })}
                  />
                ))}
              </ColorPicker>
            </FormGroup>
            
            <ModalActions>
              <Button secondary onClick={() => setShowModal(false)}>Cancel</Button>
              <Button primary onClick={handleSave} disabled={isSaving || !formData.displayName.trim()}>
                {isSaving ? <>{Icons.loader} Saving...</> : editingCategory ? <>{Icons.save} Update</> : <>{Icons.plus} Create</>}
              </Button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </ModuleContainer>
  );
};

export default KnowledgeCategoriesModule;


